import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Loader2, Pencil, CheckCircle2, Trash2, X, Plus } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { mergeLastCrearAnuncioState } from '../../utils/crearAnuncioStorage';
import { CustomSelect } from '../../components/CustomSelect';
import { getUserPlan, planAllowsMultiBrand } from '../../lib/planUtils';
import SubscriptionLimitModal from '../../components/SubscriptionLimitModal';

// Saca el header de título inicial que el LLM agrega (ej: "# Análisis Estratégico de Marca: X")
// — redundante porque el usuario ya está parado en la pill de esa marca.
// IMPORTANTE: no descartamos secciones numeradas ("1. Identidad de marca", "2. Público objetivo")
// — esas sí son contenido. Solo descartamos un título overall si aparece antes de las secciones.
const stripLeadingTitle = (text: string): string => {
  const lines = text.split('\n');
  let i = 0;
  while (i < lines.length && lines[i].trim() === '') i++;
  if (i < lines.length && /^#{1,6}\s+/.test(lines[i])) {
    const content = lines[i].replace(/^#{1,6}\s+/, '').trim();
    // Solo descartar si NO empieza con un número de sección (1., 2., 1), etc.)
    if (!/^\d+\s*[\.\)]/.test(content)) {
      i++;
      while (i < lines.length && lines[i].trim() === '') i++;
      return lines.slice(i).join('\n');
    }
  }
  return text;
};

// Simple markdown renderer for brand analysis
const renderMarkdown = (text: string): React.ReactNode => {
  const lines = stripLeadingTitle(text).split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];
  let listType: 'ul' | 'ol' | null = null;

  const flushList = () => {
    if (listItems.length > 0 && listType) {
      const ListTag = listType;
      elements.push(
        <ListTag key={elements.length} className={listType === 'ul' ? 'list-disc list-inside my-2' : 'list-decimal list-inside my-2'}>
          {listItems.map((item, i) => <li key={i}>{parseInline(item)}</li>)}
        </ListTag>
      );
      listItems = [];
      listType = null;
    }
  };

  const parseInline = (line: string): React.ReactNode => {
    // Bold: **text** or __text__
    // Italic: *text* or _text_
    const parts: React.ReactNode[] = [];
    let remaining = line;
    let key = 0;

    while (remaining.length > 0) {
      // Bold — usamos font-semibold (600) para que matchee los headers ## (que también son 600)
      const boldMatch = remaining.match(/\*\*(.+?)\*\*|__(.+?)__/);
      if (boldMatch && boldMatch.index !== undefined) {
        if (boldMatch.index > 0) {
          parts.push(remaining.slice(0, boldMatch.index));
        }
        parts.push(<strong key={key++} className="font-semibold">{boldMatch[1] || boldMatch[2]}</strong>);
        remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
        continue;
      }

      // Italic
      const italicMatch = remaining.match(/\*(.+?)\*|_(.+?)_/);
      if (italicMatch && italicMatch.index !== undefined) {
        if (italicMatch.index > 0) {
          parts.push(remaining.slice(0, italicMatch.index));
        }
        parts.push(<em key={key++}>{italicMatch[1] || italicMatch[2]}</em>);
        remaining = remaining.slice(italicMatch.index + italicMatch[0].length);
        continue;
      }

      parts.push(remaining);
      break;
    }

    return parts.length === 1 ? parts[0] : parts;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Headers (1-6 #) — todos se renderizan con el mismo estilo limpio que en el diseño viejo
    const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      flushList();
      const level = headerMatch[1].length;
      const text = headerMatch[2];
      if (level <= 2) {
        elements.push(<h3 key={elements.length} className="font-semibold text-brand-black/90 mt-4 mb-1">{parseInline(text)}</h3>);
      } else {
        elements.push(<h4 key={elements.length} className="font-semibold text-brand-black/90 mt-3 mb-1">{parseInline(text)}</h4>);
      }
      continue;
    }

    // Unordered list
    if (line.match(/^[-*]\s+/)) {
      if (listType !== 'ul') {
        flushList();
        listType = 'ul';
      }
      listItems.push(line.replace(/^[-*]\s+/, ''));
      continue;
    }

    // Ordered list
    if (line.match(/^\d+\.\s+/)) {
      if (listType !== 'ol') {
        flushList();
        listType = 'ol';
      }
      listItems.push(line.replace(/^\d+\.\s+/, ''));
      continue;
    }

    // Empty line
    if (line.trim() === '') {
      flushList();
      elements.push(<br key={elements.length} />);
      continue;
    }

    // Regular paragraph
    flushList();
    elements.push(<p key={elements.length} className="my-1">{parseInline(line)}</p>);
  }

  flushList();
  return elements;
};

interface CompanyDataProps {
  profile: any;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
}

interface CompanyProfileData {
  nombre: string | null;
  logo_url: string | null;
  rubro: string | null;
}

// Datos hardcodeados de marca (demo). Reemplazar por lectura de brand_profiles a futuro.
const BRAND_COLORS_DEMO: Array<{ hex: string; name: string; role: string }> = [
  { hex: '#000000', name: 'Negro Absoluto', role: 'primary' },
  { hex: '#B00020', name: 'Rojo Carmín', role: 'accent' },
  { hex: '#5D524D', name: 'Marrón Arcilla', role: 'secondary' },
  { hex: '#F8F5F0', name: 'Blanco Lino', role: 'neutral' },
  { hex: '#333333', name: 'Gris Grafito', role: 'neutral' },
];

const BRAND_FONTS_DEMO = {
  style: 'Serif elegante y refinada, peso regular, atemporalidad, artesanía y sofisticación discreta.',
  suggested: ['Cormorant Garamond', 'Lora'] as const,
};

const ROLE_LABELS: Record<string, string> = {
  primary: 'Principal',
  secondary: 'Secundario',
  accent: 'Acento',
  neutral: 'Neutro',
};

export const CompanyData: React.FC<CompanyDataProps> = ({ profile, setError, setSuccess }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savingName, setSavingName] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [showMultiBrandRequiredModal, setShowMultiBrandRequiredModal] = useState(false);
  const [company, setCompany] = useState<CompanyProfileData>({ nombre: '', logo_url: null, rubro: null });
  const [nameInput, setNameInput] = useState('');
  const [rubroInput, setRubroInput] = useState('Moda e Indumentaria');
  const [rubroOption, setRubroOption] = useState<string>('Moda e Indumentaria');
  const [customRubro, setCustomRubro] = useState<string>('');
  const [savingRubro, setSavingRubro] = useState(false);
  const [marketingStrategy, setMarketingStrategy] = useState<string | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isStrategyExpanded, setIsStrategyExpanded] = useState(false);
  const [isEditingAnalysis, setIsEditingAnalysis] = useState(false);
  const [analysisDraft, setAnalysisDraft] = useState('');
  const [savingAnalysis, setSavingAnalysis] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingAnalysis, setDeletingAnalysis] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Multi-marca: lista de brands del company + marca activa visualizada
  type BrandRow = {
    id: string;
    name: string;
    rubro: string | null;
    logo_url: string | null;
    is_default: boolean;
    context: string | null;
    colors: any;
    fonts: any;
    created_at: string;
  };
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [activeBrandId, setActiveBrandId] = useState<string | null>(null);
  const [showDeleteBrandModal, setShowDeleteBrandModal] = useState(false);
  const [deletingBrand, setDeletingBrand] = useState(false);

  // Pills bar: drag-to-scroll en desktop (en mobile el swipe nativo ya funciona)
  const pillsScrollRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef({
    active: false,
    startX: 0,
    startScrollLeft: 0,
    moved: false,
  });

  const onPillsMouseDown = (e: React.MouseEvent) => {
    const el = pillsScrollRef.current;
    if (!el) return;
    dragStateRef.current = {
      active: true,
      startX: e.pageX,
      startScrollLeft: el.scrollLeft,
      moved: false,
    };
  };

  const onPillsMouseMove = (e: React.MouseEvent) => {
    const state = dragStateRef.current;
    if (!state.active) return;
    const el = pillsScrollRef.current;
    if (!el) return;
    const dx = e.pageX - state.startX;
    if (Math.abs(dx) > 5) state.moved = true;
    if (state.moved) {
      e.preventDefault();
      el.scrollLeft = state.startScrollLeft - dx;
    }
  };

  const stopPillsDrag = () => {
    dragStateRef.current.active = false;
    // Limpiamos "moved" en el próximo frame para que el onClick
    // del pill pueda leerlo antes de que se resetee.
    requestAnimationFrame(() => {
      dragStateRef.current.moved = false;
    });
  };

  const activeBrand = useMemo(
    () => brands.find((b) => b.id === activeBrandId) || null,
    [brands, activeBrandId]
  );

  // Cargar Google Fonts (carga on-demand cuando hay fonts a previsualizar — manejado más abajo).

  // Cargar las brands del company + setear la activa
  const fetchBrands = useCallback(async () => {
    if (!profile?.id_company) return;
    try {
      const { data, error } = await supabase
        .from('brand_profiles')
        .select('id, name, rubro, logo_url, is_default, context, colors, fonts, created_at')
        .eq('id_company', profile.id_company)
        .eq('is_draft', false)
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: true });
      if (error) throw error;
      const list = (data as BrandRow[] | null) ?? [];
      setBrands(list);

      // Pick active brand: current user's current_brand_id → is_default → first
      const { data: { user } } = await supabase.auth.getUser();
      let pick: string | null = null;
      if (user) {
        const { data: prof } = await supabase
          .from('profiles')
          .select('current_brand_id')
          .eq('id', user.id)
          .single();
        pick = (prof as any)?.current_brand_id ?? null;
      }
      if (!pick || !list.find((b) => b.id === pick)) {
        pick = list.find((b) => b.is_default)?.id ?? list[0]?.id ?? null;
      }
      setActiveBrandId(pick);
    } catch (e) {
      // Silencioso: si falla, queda en modo legacy (sin brands)
    }
  }, [profile?.id_company]);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  // Ref para trackear qué brand id corresponde al estado actual de los inputs.
  // Sin esto, al cambiar de pill el formulario queda con valores de la marca anterior
  // por un frame y el botón "Guardar" del rubro aparece como si hubiera cambios.
  const rubroStateBrandIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!activeBrand) return;
    setNameInput(activeBrand.name || '');
    const rubroVal = activeBrand.rubro || 'Moda e Indumentaria';
    setRubroInput(rubroVal);
    if (rubros.includes(rubroVal)) {
      setRubroOption(rubroVal);
      setCustomRubro('');
    } else if (rubroVal.trim().length > 0) {
      setRubroOption('Otro');
      setCustomRubro(rubroVal);
    }
    rubroStateBrandIdRef.current = activeBrand.id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBrand?.id]);

  // Cuando cambia la marca activa, cargar Google Fonts para previsualizar tipografía
  useEffect(() => {
    const suggested = (activeBrand?.fonts as any)?.suggested as string[] | undefined;
    if (!Array.isArray(suggested) || suggested.length === 0) return;
    const families = suggested
      .map((f) => f.trim().replace(/\s+/g, '+'))
      .filter(Boolean)
      .map((f) => `family=${f}:wght@400;500;600`)
      .join('&');
    if (!families) return;
    const id = `brand-fonts-${activeBrand?.id}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
    document.head.appendChild(link);
  }, [activeBrand?.id, activeBrand?.fonts]);

  const rubros = [
    'Moda e Indumentaria',
    'Hogar y Decoración',
    'Belleza y Cuidado Personal',
    'Tecnología',
    'Salud y Bienestar',
    'Automotriz',
    'Gastronomía',
    'Juguetería',
    'Arte y Manualidades',
    'Librería',
    'Creación de Contenido',
    'Otro'
  ];

  useEffect(() => {
    const loadData = async () => {
      if (!profile?.id_company) {
        setLoading(false);
        return;
      }

      try {
        setError(null);
        // Fetch company profile (name and logo)
        const { data: companyData, error: companyError } = await supabase
          .from('company_profiles')
          .select('nombre, logo_url, rubro')
          .eq('id_company', profile.id_company)
          .single();

        if (companyError) throw companyError;

        setCompany({ nombre: companyData?.nombre ?? '', logo_url: companyData?.logo_url ?? null, rubro: companyData?.rubro ?? null });
        setNameInput(companyData?.nombre ?? '');
        const rubroVal = companyData?.rubro ?? 'Moda e Indumentaria';
        setRubroInput(rubroVal);
        if (rubros.includes(rubroVal)) {
          setRubroOption(rubroVal);
          setCustomRubro('');
        } else if (rubroVal && rubroVal.trim().length > 0) {
          setRubroOption('Otro');
          setCustomRubro(rubroVal);
        }

        // Fetch marketing strategy from company schema config row 0
        const { data: cfg, error: cfgError } = await supabase
          .from('config')
          .select('media_agent_analysis')
          .eq('id_company', profile.id_company)
          .maybeSingle();

        if (!cfgError) {
          setMarketingStrategy(cfg?.media_agent_analysis ?? null);
        } else if (cfgError.code !== 'PGRST116') {
          // Ignore no rows error; otherwise raise
          throw cfgError;
        }
      } catch (err) {
        console.error('Error loading company data:', err);
        setError('Error al cargar los datos de la empresa');
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id_company]);

  // Derived flags for Rubro UI (component scope)
  const effectiveRubro = (rubroOption === 'Otro' ? customRubro.trim() : rubroOption);
  const currentRubro = (activeBrand?.rubro ?? company.rubro ?? 'Moda e Indumentaria');
  // Solo mostrar Guardar si los inputs realmente corresponden a la marca activa
  // (evita el flash entre cambios de pill antes de que el useEffect sincronice).
  const rubroStateInSync = !activeBrand || rubroStateBrandIdRef.current === activeBrand.id;
  const showSaveRubro = rubroStateInSync
    && (rubroOption !== 'Otro' || customRubro.trim().length > 0)
    && ((effectiveRubro !== currentRubro) || savingRubro);

  const handleSaveRubro = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.id_company) return;
    try {
      setSavingRubro(true);
      setError(null);
      setSuccess(null);

      if (activeBrand) {
        const { error: brandErr } = await supabase
          .from('brand_profiles')
          .update({ rubro: rubroInput })
          .eq('id', activeBrand.id);
        if (brandErr) throw brandErr;

        if (activeBrand.is_default) {
          const { error: compErr } = await supabase
            .from('company_profiles')
            .update({ rubro: rubroInput })
            .eq('id_company', profile.id_company);
          if (compErr) throw compErr;
          setCompany((prev) => ({ ...prev, rubro: rubroInput }));
          mergeLastCrearAnuncioState({ rubro: rubroInput });
        }

        await fetchBrands();
      } else {
        const { error } = await supabase
          .from('company_profiles')
          .update({ rubro: rubroInput })
          .eq('id_company', profile.id_company);
        if (error) throw error;
        setCompany((prev) => ({ ...prev, rubro: rubroInput }));
        mergeLastCrearAnuncioState({ rubro: rubroInput });
      }

      setSuccess('Rubro actualizado correctamente');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating rubro:', err);
      setError('No se pudo actualizar el rubro');
    } finally {
      setSavingRubro(false);
    }
  };

  const handleSaveName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.id_company) return;
    const newName = nameInput.trim();
    if (!newName) {
      setError(activeBrand ? 'El nombre de la marca no puede estar vacío' : 'El nombre de la empresa no puede estar vacío');
      return;
    }
    try {
      setSavingName(true);
      setError(null);
      setSuccess(null);

      if (activeBrand) {
        // Update brand_profiles row
        const { error: brandErr } = await supabase
          .from('brand_profiles')
          .update({ name: newName })
          .eq('id', activeBrand.id);
        if (brandErr) throw brandErr;

        // Si es la default, espejear a company_profiles para compatibilidad legacy
        if (activeBrand.is_default) {
          const { error: compErr } = await supabase
            .from('company_profiles')
            .update({ nombre: newName })
            .eq('id_company', profile.id_company);
          if (compErr) throw compErr;
          setCompany((prev) => ({ ...prev, nombre: newName }));
        }

        await fetchBrands();
        setSuccess('Nombre de la marca actualizado correctamente');
      } else {
        // Modo legacy: sin brand activa, escribir solo a company_profiles
        const { error } = await supabase
          .from('company_profiles')
          .update({ nombre: newName })
          .eq('id_company', profile.id_company);
        if (error) throw error;
        setCompany((prev) => ({ ...prev, nombre: newName }));
        setSuccess('Nombre de la empresa actualizado correctamente');
      }

      setTimeout(() => setSuccess(null), 3000);
      setIsEditingName(false);
    } catch (err) {
      console.error('Error updating name:', err);
      setError('No se pudo actualizar el nombre');
    } finally {
      setSavingName(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!profile?.id_company) return;
    if (!e.target.files || !e.target.files[0]) return;

    const file = e.target.files[0];

    if (!file.type.startsWith('image/')) {
      setError('Por favor selecciona un archivo de imagen válido');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('El archivo es demasiado grande. Máximo 5MB');
      return;
    }

    try {
      setUploadingLogo(true);
      setError(null);
      setSuccess(null);

      const timestamp = new Date().getTime();
      const fileExt = file.name.split('.').pop();
      const fileName = `${timestamp}.${fileExt}`;
      const filePath = `logos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('system')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('system')
        .getPublicUrl(filePath);

      if (activeBrand) {
        const { error: brandErr } = await supabase
          .from('brand_profiles')
          .update({ logo_url: publicUrl })
          .eq('id', activeBrand.id);
        if (brandErr) throw brandErr;

        // Si es la default, espejear a company_profiles (sidebar lo lee de ahí)
        if (activeBrand.is_default) {
          const { error: compErr } = await supabase
            .from('company_profiles')
            .update({ logo_url: publicUrl })
            .eq('id_company', profile.id_company);
          if (compErr) throw compErr;
          setCompany((prev) => ({ ...prev, logo_url: publicUrl }));
        }

        await fetchBrands();
      } else {
        // Legacy: sin brand activa
        const { error: updateError } = await supabase
          .from('company_profiles')
          .update({ logo_url: publicUrl })
          .eq('id_company', profile.id_company);
        if (updateError) throw updateError;
        setCompany((prev) => ({ ...prev, logo_url: publicUrl }));
      }

      setSuccess('Logo actualizado correctamente');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError('No se pudo actualizar el logo');
    } finally {
      setUploadingLogo(false);
      // Clear the file input value
      (e.target as HTMLInputElement).value = '';
    }
  };

  // Nota: si se requiere eliminar logo, se puede reintroducir una acción que ponga logo_url en null.

  const handleDeleteBrand = async () => {
    if (!activeBrand) return;
    if (activeBrand.is_default) {
      setError('No se puede eliminar la marca principal');
      return;
    }
    if (brands.length <= 1) {
      setError('Debe quedar al menos una marca');
      return;
    }
    try {
      setDeletingBrand(true);
      setError(null);
      setSuccess(null);

      const brandIdToDelete = activeBrand.id;

      const { error: delError } = await supabase
        .from('brand_profiles')
        .delete()
        .eq('id', brandIdToDelete);

      if (delError) throw delError;

      // Pasar a la marca default antes de refrescar
      const defaultBrand = brands.find((b) => b.is_default && b.id !== brandIdToDelete);
      if (defaultBrand) {
        setActiveBrandId(defaultBrand.id);
        // Actualizar current_brand_id del usuario si apuntaba a la borrada
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from('profiles')
            .update({ current_brand_id: defaultBrand.id })
            .eq('id', user.id);
        }
      }

      setShowDeleteBrandModal(false);
      await fetchBrands();
      setSuccess('Marca eliminada correctamente');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting brand:', err);
      setError('No se pudo eliminar la marca');
    } finally {
      setDeletingBrand(false);
    }
  };

  const handleStartEditAnalysis = () => {
    setAnalysisDraft(displayAnalysis || '');
    setIsEditingAnalysis(true);
  };

  const handleCancelEditAnalysis = () => {
    setIsEditingAnalysis(false);
    setAnalysisDraft('');
  };

  const handleSaveAnalysis = async () => {
    if (!profile?.id_company) return;
    try {
      setSavingAnalysis(true);
      setError(null);
      setSuccess(null);

      const newValue = analysisDraft.trim() ? analysisDraft : null;

      if (activeBrand) {
        const { error: brandErr } = await supabase
          .from('brand_profiles')
          .update({ context: newValue })
          .eq('id', activeBrand.id);
        if (brandErr) throw brandErr;

        if (activeBrand.is_default) {
          const { error: cfgErr } = await supabase
            .from('config')
            .update({ media_agent_analysis: newValue })
            .eq('id_company', profile.id_company);
          if (cfgErr) throw cfgErr;
          setMarketingStrategy(newValue);
        }

        await fetchBrands();
      } else {
        const { error } = await supabase
          .from('config')
          .update({ media_agent_analysis: newValue })
          .eq('id_company', profile.id_company);
        if (error) throw error;
        setMarketingStrategy(newValue);
      }

      setIsEditingAnalysis(false);
      setAnalysisDraft('');
      setSuccess('Memoria de la IA actualizada');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving brand analysis:', err);
      setError('No se pudo guardar la memoria de la IA');
    } finally {
      setSavingAnalysis(false);
    }
  };

  const handleDeleteAnalysis = async () => {
    if (!profile?.id_company) return;
    try {
      setDeletingAnalysis(true);
      setError(null);
      setSuccess(null);

      if (activeBrand) {
        const { error: brandErr } = await supabase
          .from('brand_profiles')
          .update({ context: null })
          .eq('id', activeBrand.id);
        if (brandErr) throw brandErr;

        // Si es la default, también limpiar el legacy config.media_agent_analysis
        if (activeBrand.is_default) {
          const { error: cfgErr } = await supabase
            .from('config')
            .update({ media_agent_analysis: null })
            .eq('id_company', profile.id_company);
          if (cfgErr) throw cfgErr;
          setMarketingStrategy(null);
        }

        await fetchBrands();
      } else {
        const { error } = await supabase
          .from('config')
          .update({ media_agent_analysis: null })
          .eq('id_company', profile.id_company);
        if (error) throw error;
        setMarketingStrategy(null);
      }

      setShowDeleteModal(false);
      setSuccess('Análisis de marca eliminado correctamente');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting brand analysis:', err);
      setError('No se pudo eliminar el análisis de marca');
    } finally {
      setDeletingAnalysis(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-surface-card rounded-lg border border-neutral-200 p-6 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (!profile?.id_company) {
    return (
      <div className="bg-surface-card rounded-lg border border-neutral-200 p-5">
        <p className="text-[15px] text-neutral-500">Primero vinculá tu empresa en la sección Empleados.</p>
      </div>
    );
  }

  // Valores que efectivamente se muestran: si hay marca activa, usar sus datos.
  // Si no (free tier sin onboarding), fallback a company_profiles (vista legacy).
  // IMPORTANTE: cuando hay activeBrand y su logo_url es null (user salteó), NO caemos
  // a company.logo_url porque eso muestra el logo de OTRA marca (la default vieja).
  // En su lugar, dejamos null para que se muestre el placeholder de Atlas.
  const displayLogoUrl = activeBrand
    ? (activeBrand.logo_url || null)
    : (company.logo_url || null);
  const displayName = (activeBrand?.name ?? company.nombre) || '';
  const displayRubro = (activeBrand?.rubro ?? company.rubro) || '';
  const displayAnalysis = activeBrand?.context || marketingStrategy;
  const displayColors = Array.isArray(activeBrand?.colors) ? (activeBrand!.colors as any[]) : [];
  const displayFonts = (activeBrand?.fonts as any) || null;
  const canDeleteBrand = !!activeBrand && !activeBrand.is_default && brands.length > 1;

  return (
    <div className="bg-surface-card rounded-lg border border-neutral-200">
      <div className="p-5 border-b border-neutral-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-[17px] font-semibold text-brand-black/90">Mis marcas</h2>
          <p className="text-[13px] text-neutral-500 mt-0.5">
            Identidad visual y estrategia de cada una de tus marcas
          </p>
        </div>
        <button
          type="button"
          onClick={async () => {
            const plan = await getUserPlan();
            if (!planAllowsMultiBrand(plan)) {
              setShowMultiBrandRequiredModal(true);
              return;
            }
            navigate('/redes-sociales/descripcion-brand?mode=additional');
          }}
          className="inline-flex items-center justify-center gap-2 h-9 px-4 border border-neutral-200 hover:border-neutral-300 bg-white rounded-md text-[13px] font-medium text-brand-black/90 transition-colors shrink-0"
        >
          <Plus className="w-4 h-4" />
          Añadir nueva marca
        </button>
      </div>

      {/* Pills de marcas — solo visible si el user tiene >1 marca */}
      {brands.length > 1 && (
        <div className="border-b border-neutral-200">
          <div
            ref={pillsScrollRef}
            onMouseDown={onPillsMouseDown}
            onMouseMove={onPillsMouseMove}
            onMouseUp={stopPillsDrag}
            onMouseLeave={stopPillsDrag}
            className="px-5 pt-4 pb-3 overflow-x-auto cursor-grab active:cursor-grabbing select-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            <div className="flex items-center gap-2 min-w-min">
              {brands.map((b) => {
                const isActive = b.id === activeBrandId;
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={(e) => {
                      // Si venimos de un drag, no activar la pill
                      if (dragStateRef.current.moved) {
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                      }
                      setActiveBrandId(b.id);
                    }}
                    className={`inline-flex items-center gap-2 h-9 pl-1.5 pr-3 rounded-md text-[13px] font-medium transition-colors shrink-0 border ${
                      isActive
                        ? 'bg-brand-primary/10 border-brand-primary/30 text-brand-primary'
                        : 'bg-white border-neutral-200 hover:border-neutral-300 text-brand-black/80'
                    }`}
                  >
                    <img
                      src={
                        b.logo_url
                          ? (b.logo_url.includes('supabase.co')
                              ? `${b.logo_url}?width=32&height=32`
                              : b.logo_url)
                          : '/images/estudio-atlas-logo-solo.svg'
                      }
                      alt=""
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                      className="w-6 h-6 rounded-[5px] object-contain bg-white border border-neutral-200 shrink-0 pointer-events-none"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/images/estudio-atlas-logo-solo.svg';
                      }}
                    />
                    <span className="truncate max-w-[140px] pointer-events-none">{b.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="p-4 sm:p-5 space-y-3 sm:space-y-4">
        {/* Card: Logo + Nombre + Rubro */}
        <div className="bg-surface-elevated border border-neutral-200 rounded-lg p-4">
          <div className="flex items-start gap-3 sm:gap-4">
            {/* Logo a la izquierda */}
            <div className="shrink-0 flex flex-col items-center gap-2">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden border border-neutral-200 bg-white">
                <img
                  src={displayLogoUrl || 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system//Logo.svg'}
                  alt="Logo de la marca"
                  className="w-full h-full object-contain p-1"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system//Logo.svg';
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingLogo}
                className="h-7 px-2.5 rounded-md bg-surface-card border border-neutral-200 text-[11px] font-medium text-brand-black/90 hover:bg-neutral-50 shadow-sm transition-colors"
              >
                {uploadingLogo ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  'Editar'
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                id="company-logo-upload"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                disabled={uploadingLogo}
              />
            </div>

            {/* Nombre + Rubro a la derecha */}
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider mb-1.5">
                {activeBrand ? 'Nombre de la marca' : 'Nombre de la empresa'}
              </div>
              {!isEditingName ? (
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div className="text-[16px] sm:text-[18px] font-semibold text-brand-black/90 truncate">
                    {displayName || 'Sin nombre'}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setNameInput(displayName || '');
                      setIsEditingName(true);
                    }}
                    className="shrink-0 w-7 h-7 flex items-center justify-center bg-surface-card border border-neutral-200 rounded-md hover:bg-neutral-50 shadow-sm transition-colors"
                    aria-label="Editar nombre"
                    title="Editar nombre"
                  >
                    <Pencil className="w-3 h-3 text-neutral-600" />
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSaveName} className="flex flex-col gap-2 mb-3">
                  <input
                    type="text"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    placeholder="Nombre de la marca"
                    className="w-full h-9 px-3 border border-neutral-200 rounded-md text-[14px] text-brand-black/90 bg-surface-card focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="submit"
                      disabled={savingName}
                      className="h-9 px-3 bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-medium rounded-md shadow-sm transition-colors flex items-center justify-center gap-2"
                    >
                      {savingName ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Guardar'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditingName(false);
                        setNameInput(displayName || '');
                      }}
                      className="h-9 px-3 rounded-md border border-neutral-200 bg-surface-card hover:bg-neutral-50 text-[13px] font-medium text-brand-black/90 shadow-sm transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              )}

              <div className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider mb-1.5">
                Rubro
              </div>
              <form onSubmit={handleSaveRubro} className="flex flex-wrap items-center gap-2">
                <CustomSelect
                  options={rubros.map((r) => ({ value: r, label: r }))}
                  value={rubroOption}
                  onChange={(v) => {
                    setRubroOption(v);
                    if (v !== 'Otro') {
                      setCustomRubro('');
                      setRubroInput(v);
                    } else {
                      setRubroInput(customRubro);
                    }
                  }}
                  className="min-w-[180px] shrink-0"
                />
                {rubroOption === 'Otro' && (
                  <input
                    type="text"
                    value={customRubro}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCustomRubro(val);
                      setRubroInput(val);
                    }}
                    placeholder="Especificá tu rubro"
                    className="w-full h-9 px-3 rounded-md bg-surface-card border border-neutral-200 text-[14px] text-brand-black/90 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors"
                    required
                  />
                )}
                {showSaveRubro && (
                  <button
                    type="submit"
                    disabled={savingRubro}
                    className="h-9 px-3 bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-medium rounded-md shadow-sm transition-colors flex items-center justify-center gap-2"
                  >
                    {savingRubro ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Guardar'}
                  </button>
                )}
              </form>
            </div>
          </div>
        </div>

        {/* Card: Tipografía (solo si hay fonts) */}
        {displayFonts && Array.isArray(displayFonts.suggested) && displayFonts.suggested.length > 0 && (
          <div className="bg-surface-elevated border border-neutral-200 rounded-lg p-4">
            <div className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider mb-3">
              Tipografía
            </div>
            <div className="grid grid-cols-2 gap-3">
              {displayFonts.suggested.slice(0, 2).map((font: string, idx: number) => (
                <div
                  key={`${font}-${idx}`}
                  className="flex flex-col items-center justify-center text-center"
                >
                  <div
                    className="text-[40px] sm:text-[48px] leading-none text-brand-black/90 mb-1"
                    style={{ fontFamily: `"${font}", serif` }}
                  >
                    Aa
                  </div>
                  <div
                    className="text-[13px] text-brand-black/80 truncate w-full px-1"
                    style={{ fontFamily: `"${font}", serif` }}
                  >
                    {font}
                  </div>
                  <div className="text-[10px] text-neutral-500 uppercase tracking-wide mt-1">
                    {idx === 0 ? 'Principal' : 'Secundaria'}
                  </div>
                </div>
              ))}
            </div>
            {displayFonts.style && (
              <p className="text-[11px] text-neutral-500 italic pt-3 mt-3 border-t border-neutral-200/70">
                {displayFonts.style}
              </p>
            )}
          </div>
        )}

        {/* Card: Colores */}
        {displayColors.length > 0 && (
          <div className="bg-surface-elevated border border-neutral-200 rounded-lg p-4">
            <div className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider mb-3">
              Paleta de colores
            </div>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              {displayColors.slice(0, 6).map((c: any, idx: number) => (
                <div
                  key={`${c.hex}-${idx}`}
                  className="flex flex-col items-center gap-1.5"
                >
                  <div
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-neutral-200 shadow-sm"
                    style={{ backgroundColor: c.hex }}
                    title={`${c.name} · ${c.hex}`}
                  />
                  <div className="text-[9px] sm:text-[10px] text-neutral-500 font-mono">
                    {c.hex}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Card: Memoria de la IA (análisis) */}
        <div className="bg-surface-elevated border border-neutral-200 rounded-lg p-4">
          <div className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider mb-2">
            Memoria de la IA
          </div>
          {displayAnalysis ? (
            isEditingAnalysis ? (
              <div className="text-[13px] text-brand-black/90">
                <textarea
                  value={analysisDraft}
                  onChange={(e) => setAnalysisDraft(e.target.value)}
                  rows={16}
                  className="w-full rounded-md border border-neutral-200 bg-white px-3 py-2.5 text-[13px] text-brand-black/90 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary resize-y"
                />
                <div className="mt-3 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleCancelEditAnalysis}
                    disabled={savingAnalysis}
                    className="h-8 px-3.5 rounded-md bg-white hover:bg-neutral-50 text-brand-black/90 text-[13px] font-medium border border-neutral-200 transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveAnalysis}
                    disabled={savingAnalysis}
                    className="inline-flex items-center justify-center gap-1.5 h-8 px-3.5 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-medium shadow-sm transition-colors disabled:opacity-50"
                  >
                    {savingAnalysis && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    Guardar
                  </button>
                </div>
              </div>
            ) : (
              <div className="relative text-[13px] text-brand-black/80">
                <div className={`${isStrategyExpanded ? 'pb-12' : 'line-clamp-[8] pb-10'}`}>
                  {renderMarkdown(displayAnalysis)}
                </div>
                {!isStrategyExpanded && (
                  <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-surface-elevated via-surface-elevated/80 to-transparent" />
                )}
                <div className="absolute bottom-0 right-0 z-10 flex items-center gap-2">
                  {isStrategyExpanded && (
                    <button
                      type="button"
                      onClick={handleStartEditAnalysis}
                      aria-label="Editar memoria de la IA"
                      className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-white hover:bg-neutral-50 text-brand-black/90 shadow-sm border border-neutral-200 transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setIsStrategyExpanded(v => !v)}
                    className="h-8 px-3.5 rounded-md bg-white hover:bg-neutral-50 text-brand-black/90 text-[13px] font-medium shadow-sm border border-neutral-200 transition-colors"
                  >
                    {isStrategyExpanded ? 'Ver menos' : 'Ver más'}
                  </button>
                </div>
              </div>
            )
          ) : (
            <div className="text-[13px] text-neutral-500">
              No hay información disponible todavía. Completá la descripción de marca para generar el análisis.
            </div>
          )}
        </div>

        {/* Delete brand (only for non-default brands when there are multiple) */}
        {canDeleteBrand && (
          <div className="pt-1">
            <button
              type="button"
              onClick={() => setShowDeleteBrandModal(true)}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-10 px-4 border border-red-200 text-red-600 hover:bg-red-50 rounded-md text-[13px] font-medium transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Eliminar esta marca
            </button>
          </div>
        )}
      </div>

      {/* Delete Analysis Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100000] p-4" onClick={() => setShowDeleteModal(false)}>
          <div className="bg-surface-card rounded-lg shadow-lg max-w-sm w-full p-5 relative" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setShowDeleteModal(false)}
              className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center mb-5">
              <div className="mx-auto w-10 h-10 rounded-md bg-red-50 border border-red-200 flex items-center justify-center mb-3">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-[17px] font-semibold text-brand-black/90 mb-1">Eliminar análisis de marca</h3>
              <p className="text-[13px] text-neutral-500">
                Se eliminará el análisis de la marca realizado por la IA. Podés volver a generarlo desde la pestaña de <strong>Estudio Virtual</strong>.
              </p>
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="h-10 px-4 border border-neutral-200 rounded-md shadow-sm bg-surface-card text-[15px] font-medium text-brand-black/90 hover:bg-neutral-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteAnalysis}
                disabled={deletingAnalysis}
                className="h-10 px-4 bg-red-600 hover:bg-red-700 text-white text-[15px] font-medium rounded-md shadow-sm transition-colors flex items-center justify-center gap-2"
              >
                {deletingAnalysis ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Eliminar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Brand Confirmation Modal */}
      {showDeleteBrandModal && activeBrand && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100000] p-4"
          onClick={() => !deletingBrand && setShowDeleteBrandModal(false)}
        >
          <div className="bg-surface-card rounded-lg shadow-lg max-w-sm w-full p-5 relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowDeleteBrandModal(false)}
              disabled={deletingBrand}
              className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center mb-5">
              <div className="mx-auto w-10 h-10 rounded-md bg-red-50 border border-red-200 flex items-center justify-center mb-3">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-[17px] font-semibold text-brand-black/90 mb-1">Eliminar marca</h3>
              <p className="text-[13px] text-neutral-500">
                Vas a eliminar la marca <strong>{activeBrand.name}</strong>. Se perderá su identidad visual, análisis y los productos vinculados quedarán sin marca asignada. Esta acción no se puede deshacer.
              </p>
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5">
              <button
                onClick={() => setShowDeleteBrandModal(false)}
                disabled={deletingBrand}
                className="h-10 px-4 border border-neutral-200 rounded-md shadow-sm bg-surface-card text-[15px] font-medium text-brand-black/90 hover:bg-neutral-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteBrand}
                disabled={deletingBrand}
                className="h-10 px-4 bg-red-600 hover:bg-red-700 text-white text-[15px] font-medium rounded-md shadow-sm transition-colors flex items-center justify-center gap-2"
              >
                {deletingBrand ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Eliminar marca'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <SubscriptionLimitModal
        isOpen={showMultiBrandRequiredModal}
        onClose={() => setShowMultiBrandRequiredModal(false)}
        type="multi-brand-required"
      />
    </div>
  );
};
